import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SidebarAdmin from "./Utils/SidebarAdmin";
import { logout } from "../apis/backend";
import { authFetch } from "../apis/authFetch";
import apiConfig from "../config/apiConfig";
import {
  MapPin,
  PlusCircle,
  Pencil,
  Trash2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  LogOut,
  Navigation,
  Search,
} from "lucide-react";
import "./Upload.css";

interface CampusLocationItem {
  id?: string;
  campus_id: string;
  name: string;
  latitude: number | string;
  longitude: number | string;
  radius_meters: number | string;
  status: string;
}

export default function CampusLocation_Config() {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [locations, setLocations] = useState<CampusLocationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Editing state
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form State
  const [formData, setFormData] = useState<CampusLocationItem>({
    campus_id: "ECITY",
    name: "",
    latitude: "",
    longitude: "",
    radius_meters: 100,
    status: "ACTIVE",
  });

  const apiUrl = apiConfig.getResourceUrl("campus_location");

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ queryId: "GET_ALL" });
      const response = await authFetch(`${apiUrl}?${params.toString()}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch campus locations");
      }

      const data = await response.json();
      setLocations(Array.isArray(data.resource) ? data.resource : []);
    } catch (err: any) {
      console.error(err);
      setMessage({ text: err.message || "Failed to load campus locations", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleDetectGPS = () => {
    if (!navigator.geolocation) {
      setMessage({ text: "Geolocation is not supported by your browser.", type: "error" });
      return;
    }

    setMessage({ text: "Detecting current GPS coordinates...", type: "success" });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setFormData((prev) => ({
          ...prev,
          latitude: parseFloat(pos.coords.latitude.toFixed(6)),
          longitude: parseFloat(pos.coords.longitude.toFixed(6)),
        }));
        setMessage({
          text: `GPS coordinates captured: Lat ${pos.coords.latitude.toFixed(6)}, Lon ${pos.coords.longitude.toFixed(6)}`,
          type: "success",
        });
      },
      (geoErr) => {
        setMessage({ text: "GPS Access Error: " + geoErr.message, type: "error" });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.campus_id || formData.latitude === "" || formData.longitude === "") {
      setMessage({ text: "Please fill in all required fields (Campus ID, Name, Latitude, Longitude).", type: "error" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        latitude: Number(formData.latitude),
        longitude: Number(formData.longitude),
        radius_meters: Number(formData.radius_meters) || 100,
      };

      const jsonString = JSON.stringify(payload);
      const base64Encoded = btoa(jsonString);

      const params = new URLSearchParams();
      params.append("resource", base64Encoded);
      if (editingId) {
        params.append("action", "modify");
      } else {
        params.append("action", "add");
      }

      const response = await authFetch(`${apiUrl}?${params.toString()}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to ${editingId ? "update" : "create"} campus location.`);
      }

      setMessage({
        text: `Campus Location successfully ${editingId ? "updated" : "created"}!`,
        type: "success",
      });

      // Reset form
      setFormData({
        campus_id: "ECITY",
        name: "",
        latitude: "",
        longitude: "",
        radius_meters: 100,
        status: "ACTIVE",
      });
      setEditingId(null);

      // Refresh table
      fetchLocations();
    } catch (err: any) {
      console.error(err);
      setMessage({ text: err.message || "Failed to save location", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (loc: CampusLocationItem) => {
    setEditingId(loc.id || null);
    setFormData({
      id: loc.id,
      campus_id: loc.campus_id || "ECITY",
      name: loc.name || "",
      latitude: loc.latitude !== undefined ? loc.latitude : "",
      longitude: loc.longitude !== undefined ? loc.longitude : "",
      radius_meters: loc.radius_meters || 100,
      status: loc.status || "ACTIVE",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this campus location?")) return;

    try {
      const payload = { id };
      const jsonString = JSON.stringify(payload);
      const base64Encoded = btoa(jsonString);

      const params = new URLSearchParams();
      params.append("resource", base64Encoded);
      params.append("action", "delete");

      const response = await authFetch(`${apiUrl}?${params.toString()}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete campus location");
      }

      setMessage({ text: "Campus location deleted successfully", type: "success" });
      fetchLocations();
    } catch (err: any) {
      console.error(err);
      setMessage({ text: err.message || "Failed to delete location", type: "error" });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({
      campus_id: "ECITY",
      name: "",
      latitude: "",
      longitude: "",
      radius_meters: 100,
      status: "ACTIVE",
    });
  };

  const handleLogout = async () => {
    const ok = await logout();
    if (ok) {
      navigate("/");
    }
  };

  const filteredLocations = locations.filter((loc) => {
    const query = searchQuery.toLowerCase();
    return (
      (loc.name && loc.name.toLowerCase().includes(query)) ||
      (loc.campus_id && loc.campus_id.toLowerCase().includes(query))
    );
  });

  return (
    <div className="page12Container">
      <SidebarAdmin
        sidebarCollapsed={sidebarCollapsed}
        toggleSidebar={() => setSidebarCollapsed((prev) => !prev)}
        activeSection="dashboard"
      />

      <main className={`mainContent ${sidebarCollapsed ? "sidebarCollapsed" : ""}`}>
        <header className="contentHeader d-flex justify-content-between align-items-center">
          <h1 className="pageTitle d-flex align-items-center gap-2">
            <MapPin className="text-danger" size={28} /> Campus Locations Configuration
          </h1>

          <div className="userProfile" style={{ position: "relative" }}>
            <div
              className="profileCircle"
              onClick={() => setShowDropdown((prev) => !prev)}
              style={{ cursor: "pointer" }}
            >
              <span className="profileInitial">A</span>
            </div>

            {showDropdown && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "45px",
                  background: "white",
                  borderRadius: "8px",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                  padding: "10px",
                  minWidth: "130px",
                  zIndex: 100,
                }}
              >
                <button
                  onClick={handleLogout}
                  style={{
                    width: "100%",
                    background: "none",
                    border: "none",
                    textAlign: "left",
                    padding: "8px",
                    cursor: "pointer",
                    fontSize: "16px",
                  }}
                >
                  <LogOut size={16} />
                  <span style={{ marginLeft: "8px" }}>Logout</span>
                </button>
              </div>
            )}
          </div>
        </header>

        <div className="contentBody p-4">
          {message && (
            <div
              className={`alert ${message.type === "success" ? "alert-success" : "alert-danger"} alert-dismissible fade show`}
              role="alert"
            >
              {message.type === "success" ? <CheckCircle2 className="me-2" size={18} /> : <AlertCircle className="me-2" size={18} />}
              {message.text}
              <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
            </div>
          )}

          {/* FORM SECTION: CREATE / EDIT CAMPUS LOCATION */}
          <section className="card shadow-sm border-0 mb-4 p-4">
            <h2 className="fs-5 fw-bold mb-3 text-primary d-flex align-items-center gap-2">
              {editingId ? <Pencil size={22} /> : <PlusCircle size={22} />}
              {editingId ? "Edit Campus Location" : "Add New Campus Location"}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Campus ID *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. ECITY"
                    value={formData.campus_id}
                    onChange={(e) => setFormData({ ...formData, campus_id: e.target.value })}
                    required
                  />
                  <small className="text-muted">Identifier linking course programs to this campus</small>
                </div>

                <div className="col-md-5">
                  <label className="form-label fw-semibold">Location / Room Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Electronics City - Main Auditorium"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">Status *</label>
                  <select
                    className="form-select"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    required
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Latitude *</label>
                  <input
                    type="number"
                    step="any"
                    className="form-control"
                    placeholder="e.g. 12.839930"
                    value={formData.latitude}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                    required
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Longitude *</label>
                  <input
                    type="number"
                    step="any"
                    className="form-control"
                    placeholder="e.g. 77.664820"
                    value={formData.longitude}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                    required
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Radius (Meters) *</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="100"
                    value={formData.radius_meters}
                    onChange={(e) => setFormData({ ...formData, radius_meters: e.target.value })}
                    required
                  />
                  <small className="text-muted">Allowed attendance GPS threshold around point</small>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-4 pt-2 border-top">
                <button
                  type="button"
                  className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                  onClick={handleDetectGPS}
                >
                  <Navigation size={18} className="text-primary" /> Auto-Detect Current GPS Coordinates
                </button>

                <div className="d-flex gap-2">
                  {editingId && (
                    <button type="button" className="btn btn-light" onClick={handleCancelEdit}>
                      Cancel
                    </button>
                  )}
                  <button type="submit" className="btn btn-primary px-4 d-inline-flex align-items-center gap-2" disabled={saving}>
                    {editingId ? <Pencil size={18} /> : <PlusCircle size={18} />}
                    {saving ? "Saving..." : editingId ? "Update Location" : "Save Campus Location"}
                  </button>
                </div>
              </div>
            </form>
          </section>

          {/* TABLE SECTION: REGISTERED CAMPUS LOCATIONS */}
          <section className="card shadow-sm border-0 p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 className="fs-5 fw-bold mb-0 d-flex align-items-center gap-2">
                <MapPin size={22} className="text-success" /> Registered Campus Locations
              </h3>

              <div className="d-flex align-items-center gap-3">
                <div className="input-group" style={{ width: "260px" }}>
                  <span className="input-group-text bg-white border-end-0">
                    <Search size={16} className="text-muted" />
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Search by name or campus..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <button className="btn btn-outline-secondary d-flex align-items-center gap-1" onClick={fetchLocations} title="Refresh Locations">
                  <RefreshCw size={16} />
                </button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Campus ID</th>
                    <th>Location Name</th>
                    <th>Latitude</th>
                    <th>Longitude</th>
                    <th>Radius (m)</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        Loading locations...
                      </td>
                    </tr>
                  ) : filteredLocations.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        No campus locations found. Add your first location using the form above.
                      </td>
                    </tr>
                  ) : (
                    filteredLocations.map((loc) => (
                      <tr key={loc.id}>
                        <td>
                          <span className="badge bg-light text-dark border fw-bold">{loc.campus_id}</span>
                        </td>
                        <td className="fw-semibold">{loc.name}</td>
                        <td><code>{loc.latitude}</code></td>
                        <td><code>{loc.longitude}</code></td>
                        <td>{loc.radius_meters || 100} m</td>
                        <td>
                          <span className={`badge ${loc.status === "ACTIVE" ? "bg-success" : "bg-secondary"}`}>
                            {loc.status || "ACTIVE"}
                          </span>
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEdit(loc)}
                            title="Edit Location"
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => loc.id && handleDelete(loc.id)}
                            title="Delete Location"
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
